import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '../store/auth.store';
import { AuthResponse, AuthTokens } from '../types/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3100/v1';
const CLIENT_ID = process.env.REACT_APP_CLIENT_ID || 'web';

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 20_000,
  // Envoie le cookie httpOnly du refresh token (bug 1.4) sur chaque requête —
  // notamment /auth/refresh, qui n'a plus besoin du token dans le body.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Tickart-Client': CLIENT_ID,
  },
});

/* ----- Request : injection JWT ----- */
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Uploads multipart : on retire le Content-Type par défaut (application/json)
  // pour que le navigateur pose lui-même `multipart/form-data; boundary=…`.
  // Sinon le serveur ne peut pas parser le fichier (upload qui « ne marche pas »).
  if (config.data instanceof FormData && config.headers) {
    delete (config.headers as Record<string, unknown>)['Content-Type'];
  }
  return config;
});

/* ----- Response : auto-refresh sur 401 ----- */

let refreshPromise: Promise<AuthTokens | null> | null = null;

const performRefresh = async (): Promise<AuthTokens | null> => {
  // Le refresh token voyage dans un cookie httpOnly (bug 1.4) — plus besoin de
  // le lire depuis le store, ni de l'envoyer dans le body.
  try {
    const response = await axios.post<AuthResponse>(
      `${API_URL}/auth/refresh`,
      {},
      { withCredentials: true, headers: { 'Content-Type': 'application/json' } },
    );
    useAuthStore.getState().setSession(response.data);
    return { accessToken: response.data.accessToken };
  } catch (err) {
    // Un vrai refus (401/403 : refresh token expiré/invalide, ou réutilisation
    // détectée côté serveur — auth.service.ts refresh()) invalide bien la
    // session. Mais un aléa réseau/serveur (timeout, 5xx, backend qui redémarre
    // pendant un déploiement…) ne prouve PAS que le refresh token est invalide —
    // avant ce correctif, N'IMPORTE QUELLE erreur ici déconnectait l'utilisateur,
    // y compris une coupure réseau d'une seconde ou un simple redéploiement API :
    // « la session se ferme toute seule » alors qu'elle restait valide.
    const status = (err as AxiosError).response?.status;
    if (status === 401 || status === 403) {
      useAuthStore.getState().logout();
    }
    return null;
  }
};

/**
 * Point d'entrée UNIQUE pour rafraîchir la session — à usage de l'intercepteur
 * 401 ci-dessous ET de `useAuthStore.hydrate()` (restauration de session au
 * chargement de l'app). Le refresh token est à usage unique et tourne à chaque
 * appel (voir auth.service.ts `refresh()` — rotation + détection de réutilisation) :
 * deux appels concurrents utilisant le même cookie de départ se neutralisent
 * l'un l'autre côté serveur (le perdant déclenche la détection de réutilisation
 * et invalide TOUTE la session, y compris côté gagnant). `hydrate()` déclenchait
 * autrefois son propre appel axios brut, non dédupliqué avec celui-ci : sur un
 * double montage (React.StrictMode), un focus de fenêtre concurrent, ou
 * plusieurs onglets, la course cassait silencieusement la session (déconnexion
 * différée à la prochaine expiration de l'access token — ex. en pleine
 * création d'événement). Un seul point d'entrée + une seule promesse en vol
 * élimine la course.
 */
export const refreshSession = async (): Promise<AuthTokens | null> => {
  if (!refreshPromise) refreshPromise = performRefresh();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/auth/')
    ) {
      original._retry = true;
      const hadSession = useAuthStore.getState().isAuthenticated;
      const tokens = await refreshSession();
      if (tokens) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${tokens.accessToken}`;
        return api(original);
      }
      // Refresh impossible : on ne force la redirection /login que si
      // performRefresh() a RÉELLEMENT invalidé la session (401/403 confirmé —
      // voir son commentaire). Sur un aléa réseau/serveur, la session reste
      // intacte (isAuthenticated toujours vrai) : on laisse juste CETTE requête
      // échouer, sans déconnecter l'utilisateur pour un incident transitoire.
      if (hadSession && !useAuthStore.getState().isAuthenticated) {
        window.dispatchEvent(new CustomEvent('tickart:session-expired'));
      }
    }
    return Promise.reject(error);
  },
);

/** Helper typé pour les payloads génériques. */
export const request = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const response = await api.request<T>(config);
  return response.data;
};
